// Lib
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';

// Util
import { getId } from '../../../../../../../common/users/userHelper';
import { getPopupSelectorId } from './editorPermissionPopup/boardEditorPermissionUtils';
import { hasChanged } from '../../../../../../utils/react/propsComparisons';

// Actions
import { addUserAsEditor } from './boardEditorPanelActions';

// Components
import Button from '../../../../../../components/buttons/Button';
import Icon from '../../../../../../components/icons/Icon';
import Spinner from '../../../../../../components/loaders/Spinner';
import PopupTrigger from '../../../../../../components/popupPanel/PopupTrigger';
import BoardEditorPermissionPopup from './editorPermissionPopup/BoardEditorPermissionPopup';
import BoardEditorListEntryStatus from './editorList/BoardEditorListEntryStatus';
import MobileBoardEditorPermissionSheet from './editorPermissionSheet/MobileBoardEditorPermissionSheet';

// Styles
import './BoardEditorPermissionUtil.scss';

const updatingStateChanged = hasChanged('updatingState');

const ERROR_DISPLAY_TIME = 2000;

const BoardEditorPermissionUtilButton = (props) => {
    const { show, isPhoneOrMobileAppMode } = props;

    return (
        <div className="button-content" onClick={(e) => e.stopPropagation()}>
            <div className="button-text">
                <BoardEditorListEntryStatus {...props} />
            </div>
            {show && !isPhoneOrMobileAppMode && (
                <PopupTrigger popupId={getPopupSelectorId(props)}>{() => <Icon name="menu-down-arrow" />}</PopupTrigger>
            )}
        </div>
    );
};

const BoardEditorPermissionUtilTryAgain = ({ dispatchAddUserAsEditor, user }) => {
    const retrySubmission = () => dispatchAddUserAsEditor({ user });

    return (
        <Button onClickFn={retrySubmission} className="secondary">
            <div className="button-content">
                <Icon name="link-fail" />
                <div className="button-text error link">try again</div>
            </div>
        </Button>
    );
};

const BoardEditorPermissionUtilUpdating = ({ updatingState }) => (
    <Button className="secondary">
        <div className="button-content">
            <div className="button-text">{updatingState}...</div>
            <Spinner show />
        </div>
    </Button>
);

BoardEditorPermissionUtilUpdating.propTypes = {
    updatingState: PropTypes.string,
};

const BoardEditorPermissionUtilError = ({ clearError }) => (
    <Button onClickFn={clearError} className="secondary">
        <div className="button-content">
            <Icon name="link-fail" />
            <div className="button-text error">update failed</div>
        </div>
    </Button>
);

BoardEditorPermissionUtilError.propTypes = {
    clearError: PropTypes.func,
};

const mapDispatchToProps = (dispatch) => ({
    dispatchAddUserAsEditor: ({ user }) => dispatch(addUserAsEditor({ user })),
});

@connect(null, mapDispatchToProps)
class BoardEditorPermissionUtil extends React.Component {
    state = {
        error: null,
    };

    componentWillReceiveProps(nextProps) {
        if (updatingStateChanged(this.props, nextProps) && nextProps.updatingState === 'error') {
            this.setState({ error: true });

            this.clearErrorTimeout();
            this.errorTimeout = setTimeout(() => this.setState({ error: null }), ERROR_DISPLAY_TIME);
        }
    }

    componentWillUnmount() {
        this.clearErrorTimeout();
    }

    clearError = () => {
        this.clearErrorTimeout();
        this.setState({ error: null });
    };

    clearErrorTimeout = () => this.errorTimeout && clearTimeout(this.errorTimeout);

    render() {
        const { updatingState, user, show, currentlyShared, isPhoneOrMobileAppMode } = this.props;

        let child;

        const userId = getId(user);

        if (this.state.error) {
            child = <BoardEditorPermissionUtilError {...this.props} clearError={this.clearError} />;
        } else if (updatingState && updatingState !== 'error') {
            child = <BoardEditorPermissionUtilUpdating {...this.props} />;
        } else if (updatingState === 'error' && !currentlyShared) {
            child = <BoardEditorPermissionUtilTryAgain {...this.props} />;
        } else {
            child = <BoardEditorPermissionUtilButton {...this.props} userId={userId} />;
        }

        return (
            <div className={classNames('BoardEditorPermissionUtil', { show })} onClick={(e) => e.stopPropagation()}>
                {child}
                {isPhoneOrMobileAppMode ? (
                    <MobileBoardEditorPermissionSheet {...this.props} userId={userId} />
                ) : (
                    <BoardEditorPermissionPopup
                        {...this.props}
                        userId={userId}
                        popupId={getPopupSelectorId({ user })}
                    />
                )}
            </div>
        );
    }
}

BoardEditorPermissionUtilTryAgain.propTypes =
    BoardEditorPermissionUtil.propTypes =
    BoardEditorPermissionUtilButton.propTypes =
        {
            currentlyViewing: PropTypes.bool,
            openPopup: PropTypes.func,
            show: PropTypes.bool,
            user: PropTypes.object,
            dispatchAddUserAsEditor: PropTypes.func,
            isPhoneOrMobileAppMode: PropTypes.bool,
            userId: PropTypes.string,
        };

export default BoardEditorPermissionUtil;
